/**
 * SendMessageModal — send one user a direct message.
 *
 * Backend: POST /console/users/{id}/message with `{ title, body, imageUrl }`. The message
 * is delivered as a published, one-user What's New card (see
 * docs/FeatureDesigns/admin-direct-user-message.md), so it appears the next time
 * the user opens the app.
 *
 * Auth: caller gates visibility; the backend requires content_admin on the
 * target user's tenant (super-admin: any user).
 */

import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Modal } from '@shared-ui/components';
import { ImageUploadField } from '@shared-ui/question-edit-form';
import { userApi } from '../../api/userApi';
import { adminApi } from '../../api/adminApi';

interface SendMessageModalProps {
  isOpen: boolean;
  userId: string;
  userName: string;
  onClose: () => void;
}

export function SendMessageModal({ isOpen, userId, userName, onClose }: SendMessageModalProps) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  // Reset the form each time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setTitle('');
    setBody('');
    setImageUrl(null);
    setError('');
    setSent(false);
  }, [isOpen]);

  const sendMut = useMutation({
    mutationFn: () => userApi.sendUserMessage(userId, { title: title.trim(), body: body.trim(), imageUrl: imageUrl ?? undefined }),
    onSuccess: () => setSent(true),
    onError: (err: Error) => setError(err.message),
  });

  // A message needs content — an empty card isn't a message. An image alone
  // counts: a screenshot with no caption is still something to say.
  const canSend = title.trim().length > 0 || body.trim().length > 0 || !!imageUrl;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Send message — ${userName}`}
      size="md"
      footer={
        sent ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              onClick={() => { setError(''); sendMut.mutate(); }}
              disabled={!canSend || sendMut.isPending}
              loading={sendMut.isPending}
            >
              Send message
            </Button>
          </>
        )
      }
    >
      {sent ? (
        <div style={{ padding: 'var(--spacing-sm)', background: 'var(--status-success-bg, #e6f5ea)', color: 'var(--status-success-text, #1a7f37)', borderRadius: 4 }}>
          Message sent. It appears as a What's New card the next time {userName} opens the app.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 'var(--spacing-sm)' }}>
            Delivered as a personal What's New card — shown once, the next time this user
            opens the app. A title or message is required.
          </div>

          <label style={{ display: 'block', marginBottom: 'var(--spacing-sm)' }}>
            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Title</div>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              disabled={sendMut.isPending}
              placeholder="Optional heading"
              maxLength={120}
              style={{ width: '100%', padding: 'var(--spacing-sm)', borderRadius: 4, border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
            />
          </label>

          <label style={{ display: 'block' }}>
            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Message</div>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              disabled={sendMut.isPending}
              placeholder="What do you want to tell this user? (markdown supported)"
              rows={5}
              style={{ width: '100%', padding: 'var(--spacing-sm)', borderRadius: 4, border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
            />
          </label>

          {/* Same control as question edit, so paste-from-clipboard works the
              way it does there — screenshots are the usual case here. */}
          <div style={{ marginTop: 'var(--spacing-sm)' }}>
            <ImageUploadField
              label="Image (optional)"
              value={imageUrl}
              onChange={setImageUrl}
              maxPreviewHeight={160}
              onUploadImage={(base64, mimeType) => adminApi.uploadQuestionImage(base64, mimeType)}
            />
          </div>

          {error && (
            <div style={{ marginTop: 'var(--spacing-sm)', padding: 'var(--spacing-sm)', background: 'var(--status-error-bg)', color: 'var(--status-error-text)', borderRadius: 4 }}>
              {error}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
